/**
 * Settings Page
 * User and application settings
 */

import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Icon } from '../components/icons';
import { apiClient } from '../lib/api';
import { useToast } from '../components/ui/ToastProvider';

export function Settings() {
  const { user } = useAuth();
  const [success, setSuccess] = useState('');
  const [isResending, setIsResending] = useState(false);
  const { showToast } = useToast();

  const handleSaveProfile = () => {
    setSuccess('Profile updated successfully!');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleResendVerification = async () => {
    setIsResending(true);
    try {
      const res = await apiClient.resendVerification();
      if (res.success) {
        showToast('Verification email sent', 'success');
      } else {
        showToast(res.error || 'Failed to resend verification', 'error');
      }
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to resend verification', 'error');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="max-w-4xl space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600 mt-1">
          Manage your account and application preferences
        </p>
      </div>

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}

      {/* Profile Settings */}
      <Card>
        <CardHeader title="Profile Settings" />
        <div className="space-y-4">
          <div className="flex items-center gap-4 pb-4 border-b border-gray-200">
            <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-medium">
              {user?.name?.[0] || user?.email[0].toUpperCase()}
            </div>
            <div>
              <h3 className="font-medium text-gray-900">{user?.name || user?.email}</h3>
              <p className="text-sm text-gray-500 capitalize">{user?.role}</p>
            </div>
          </div>

          <Input
            label="Name"
            defaultValue={user?.name || ''}
            placeholder="Your name"
          />

          <Input
            label="Email"
            type="email"
            defaultValue={user?.email}
            disabled
            helperText="Email cannot be changed"
          />

          <div className="flex items-center gap-2 pt-4">
            <Button onClick={handleSaveProfile}>
              <Icon name="user" size="sm" className="mr-2" />
              Save Profile
            </Button>
          </div>
        </div>
      </Card>

      {/* Security Settings */}
      <Card>
        <CardHeader title="Security" />
        <div className="space-y-4">
          {user?.is_verified === false && (
            <div className="p-4 border border-amber-200 bg-amber-50 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Icon name="alert" size="md" className="text-amber-600" />
                <div>
                  <p className="font-medium text-amber-800">Email not verified</p>
                  <p className="text-sm text-amber-700">Resend the verification email to unlock full access.</p>
                </div>
              </div>
              <Button
                size="sm"
                variant="secondary"
                onClick={handleResendVerification}
                isLoading={isResending}
              >
                Resend
              </Button>
            </div>
          )}

          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded">
                <Icon name="key" size="md" className="text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Password</p>
                <p className="text-sm text-gray-500">
                  Last changed 30 days ago
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm">
              Change Password
            </Button>
          </div>

          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded">
                <Icon name="bell" size="md" className="text-green-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Email Notifications</p>
                <p className="text-sm text-gray-500">
                  Receive notifications about posts
                </p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" defaultChecked />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>
      </Card>

      {/* Brand Access */}
      <Card>
        <CardHeader
          title="Brand Access"
          subtitle={`You have access to ${user?.brands.length || 0} brands`}
        />
        <div className="space-y-2">
          {user?.brands.map((brandId) => (
            <div
              key={brandId}
              className="flex items-center justify-between p-3 border border-gray-200 rounded-lg"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gray-200 rounded" />
                <span className="font-medium text-gray-900 capitalize">
                  {brandId}
                </span>
              </div>
              <span className="text-sm text-green-600">Active</span>
            </div>
          ))}
        </div>
      </Card>

      {/* Preferences */}
      <Card>
        <CardHeader title="Preferences" />
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Language</p>
              <p className="text-sm text-gray-500">
                Choose your preferred language
              </p>
            </div>
            <select className="px-3 py-2 border border-gray-300 rounded-lg">
              <option>English</option>
              <option>Spanish</option>
              <option>French</option>
            </select>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Timezone</p>
              <p className="text-sm text-gray-500">
                Set your local timezone for scheduling
              </p>
            </div>
            <select className="px-3 py-2 border border-gray-300 rounded-lg">
              <option>UTC</option>
              <option>EST</option>
              <option>PST</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Danger Zone */}
      <Card>
        <CardHeader title="Danger Zone" />
        <div className="space-y-4">
          <div className="p-4 border-2 border-red-200 rounded-lg bg-red-50">
            <h4 className="font-medium text-red-900 mb-2">Delete Account</h4>
            <p className="text-sm text-red-700 mb-4">
              Once you delete your account, there is no going back. Please be certain.
            </p>
            <Button variant="danger" size="sm">
              Delete Account
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
